import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Link2, File, X, Check, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { 
  uploadDocument, 
  linkOpportunityDocument, 
  getOpportunityDocuments,
  getClaimDocuments 
} from '../../../api/claims';

const ALLOWED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'image/png': '.png',
  'image/jpeg': '.jpg, .jpeg'
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const DocumentSelector = ({ claimId, stageName, onDocumentAdded }) => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [selectedOppDocs, setSelectedOppDocs] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  // Fetch opportunity documents
  const { data: oppDocsData, isLoading: oppDocsLoading } = useQuery({
    queryKey: ['opportunityDocuments', claimId],
    queryFn: () => getOpportunityDocuments(claimId)
  });

  // Fetch current claim documents
  const { data: claimDocsData } = useQuery({
    queryKey: ['claimDocuments', claimId, stageName],
    queryFn: () => getClaimDocuments(claimId, stageName)
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file) => uploadDocument(claimId, file, stageName),
    onSuccess: () => {
      queryClient.invalidateQueries(['claimDocuments', claimId]);
      setSelectedFile(null);
      if (onDocumentAdded) onDocumentAdded();
    }
  });

  // Link mutation
  const linkMutation = useMutation({
    mutationFn: (docId) => linkOpportunityDocument(claimId, docId, stageName),
    onSuccess: () => {
      queryClient.invalidateQueries(['claimDocuments', claimId]);
      queryClient.invalidateQueries(['opportunityDocuments', claimId]);
      if (onDocumentAdded) onDocumentAdded();
    }
  });

  const validateFile = (file) => {
    if (!file) return 'No file selected';
    
    if (!Object.keys(ALLOWED_FILE_TYPES).includes(file.type)) {
      return 'File type not allowed. Allowed: PDF, DOCX, XLSX, PNG, JPG';
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    
    return null;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateFile(file);
      setFileError(error);
      if (!error) {
        setSelectedFile(file);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const error = validateFile(file);
      setFileError(error);
      if (!error) {
        setSelectedFile(file);
      }
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const handleLinkDocuments = () => {
    selectedOppDocs.forEach(docId => {
      linkMutation.mutate(docId);
    });
    setSelectedOppDocs([]);
  };

  const toggleOppDoc = (docId) => {
    setSelectedOppDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const oppDocs = oppDocsData?.data || [];
  const claimDocs = claimDocsData?.data || [];

  return (
    <div className="space-y-4">
      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">Upload New</TabsTrigger>
          <TabsTrigger value="link">Link from Pursuit</TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-sm text-gray-600 mb-2">
              Drag and drop your file here, or click to browse
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Allowed: PDF, DOCX, XLSX, PNG, JPG (Max 10MB)
            </p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('file-upload').click()}
            >
              Browse Files
            </Button>
          </div>

          {fileError && (
            <Alert variant="destructive">
              <AlertDescription>{fileError}</AlertDescription>
            </Alert>
          )}

          {selectedFile && !fileError && (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <File className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                >
                  {uploadMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    setFileError(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Link Tab */}
        <TabsContent value="link" className="space-y-4">
          {oppDocsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-600">Loading documents...</span>
            </div>
          ) : oppDocs.length === 0 ? (
            <Alert>
              <AlertDescription>
                No documents found in the linked pursuit. Upload files from the "Upload New" tab.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {oppDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                      doc.is_linked ? 'bg-gray-50 border-gray-200' : 'hover:bg-gray-50 cursor-pointer'
                    }`}
                    onClick={() => !doc.is_linked && toggleOppDoc(doc.id)}
                  >
                    <Checkbox
                      checked={selectedOppDocs.includes(doc.id) || doc.is_linked}
                      disabled={doc.is_linked}
                      onCheckedChange={() => toggleOppDoc(doc.id)}
                    />
                    <File className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(doc.file_size_bytes)} • {doc.document_type}
                      </p>
                    </div>
                    {doc.is_linked && (
                      <Badge variant="secondary" className="gap-1">
                        <Check className="h-3 w-3" />
                        Linked
                      </Badge>
                    )}
                  </div>
                ))}
              </div>

              {selectedOppDocs.length > 0 && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-sm text-gray-600">
                    {selectedOppDocs.length} document{selectedOppDocs.length !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    size="sm"
                    onClick={handleLinkDocuments}
                    disabled={linkMutation.isPending}
                  >
                    {linkMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Linking...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Link Selected
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Current Documents */}
      {claimDocs.length > 0 && (
        <div className="pt-4 border-t">
          <Label className="text-sm font-medium mb-2 block">Current Documents for this Stage</Label>
          <div className="space-y-2">
            {claimDocs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                <File className="h-4 w-4 text-blue-600" />
                <span className="flex-1 truncate">{doc.file_name}</span>
                <Badge variant="outline" className="text-xs">
                  {doc.is_opportunity_document ? 'Linked' : 'Uploaded'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSelector;
